import React, { useEffect, useMemo, useState } from "react";
import { AdminCandidateListRow } from "../admin-types";
import { adminService } from "../admin.service";
import {
  Column,
  Empty,
  Panel,
  Table,
  SortConfig,
  SortDirection,
} from "../AdminUI";

interface Props {
  onSelectCandidate: (c: AdminCandidateListRow) => void;
  cached: AdminCandidateListRow[] | null;
  onCache: (data: AdminCandidateListRow[]) => void;
  refreshKey: number;
}

const normalizeRows = (rows: AdminCandidateListRow[]) =>
  rows.map((row) => ({
    ...row,
    user_id: row.user_id || row.user,
    candidate_id: row.candidate_id || row.id,
  }));

export default function CandidatesList({
  onSelectCandidate,
  cached,
  onCache,
  refreshKey,
}: Props) {
  const PAGE_SIZE_OPTIONS = [25, 50, 100];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<AdminCandidateListRow[]>(
    cached || []
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  const orderBy = useMemo(() => {
    if (!sortConfig?.key || !sortConfig?.direction) return "";
    return sortConfig.direction === "desc"
      ? `-${sortConfig.key}`
      : sortConfig.key;
  }, [sortConfig]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await adminService.fetchCandidates({
          page,
          size: pageSize,
          search: debouncedSearch,
          orderBy,
        });
        if (!mounted) return;
        const normalized = normalizeRows(result.rows || []);
        if (normalized.length > 0) {
          const sample = normalized[0];
          console.log("[Admin] candidates list sample", {
            id: sample.id,
            user_id: sample.user_id,
            candidate_id: sample.candidate_id,
            user: (sample as any).user,
          });
        }
        setCandidates(normalized);
        setTotal(result.total || 0);
        setHasNext(result.hasNext);
        setHasPrevious(result.hasPrevious);
        onCache(normalized);
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [refreshKey, page, pageSize, debouncedSearch, orderBy]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const cols: Column<AdminCandidateListRow>[] = [
    {
      key: "first_name",
      header: "Nombre",
      sortable: true,
      render: (r) => <span className="font-medium">{r.first_name || "—"}</span>,
    },
    { key: "last_name", header: "Apellido", sortable: true },
    { key: "email", header: "Email", sortable: true },
    { key: "phone", header: "Teléfono", sortable: true },
    { key: "nif", header: "NIF", sortable: true },
    { key: "city", header: "Ciudad", sortable: true },
    { key: "country", header: "País", sortable: true },
    {
      key: "created_at",
      header: "Creado",
      sortable: true,
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
    },
  ];

  return (
    <Panel
      title="Candidatos"
      right={
        <div className="admin-list-controls">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar candidatos..."
            className="input"
          />
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="admin-page-size"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} / pág.
              </option>
            ))}
          </select>
          <span className="admin-list-meta">{total} resultados</span>
        </div>
      }
    >
      {loading ? (
        <Empty>Cargando candidatos…</Empty>
      ) : error ? (
        <Empty>Error: {error}</Empty>
      ) : candidates.length ? (
        <>
          <Table
            columns={cols}
            rows={candidates}
            keyField="id"
            onRowClick={onSelectCandidate}
            onSort={handleSort}
            sortConfig={sortConfig}
          />
          <div className="admin-pagination">
            <button
              className="action-btn ghost"
              disabled={!hasPrevious || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <span className="admin-pagination-info">
              Página {page} de {totalPages}
            </span>
            <button
              className="action-btn ghost"
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </>
      ) : (
        <Empty>No se encontraron candidatos.</Empty>
      )}
    </Panel>
  );
}
